import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Loader2, Plus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

const fmtZeit = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ENDPOINTS = [
  ["GET/POST", "/api/erp/lieferanten", "Lieferanten lesen / anlegen & aktualisieren (Upsert je Liefernummer)"],
  ["GET/POST", "/api/erp/benutzer", "Benutzer inkl. Liefernummern-Zuordnung (Upsert je E-Mail)"],
  ["GET/POST", "/api/erp/lieferungen", "Liefermengen lesen / einspielen (Duplikat-Erkennung)"],
  ["GET/POST", "/api/erp/laborwerte", "Laborwerte lesen / einspielen (Upsert je Probenummer)"],
  ["GET", "/api/erp/abrechnungen", "Abrechnungsdaten inkl. Positionen lesen (Filter: monat, liefernummer)"],
];

export default function AdminApiKeysPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/api-keys").then((res) => setRows(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const erstellen = async () => {
    setCreating(true);
    try {
      await api.post("/api-keys", { name });
      toast.success("API-Key erstellt.");
      setName("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  const loeschen = async (r) => {
    try {
      await api.delete(`/api-keys/${r.id}`);
      toast.success("API-Key gelöscht. Zugriffe mit diesem Key sind ab sofort ungültig.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const kopieren = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("API-Key in die Zwischenablage kopiert.");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">API-Keys (ERP-Schnittstelle)</h1>
        <p className="text-sm text-muted-foreground">Bidirektionaler Datenaustausch mit dem ERP über die REST-API. Authentifizierung per Header <code className="font-num text-xs bg-secondary px-1.5 py-0.5 rounded">X-API-Key</code>.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Neuen API-Key erstellen</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Bezeichnung</Label>
            <Input data-testid="api-key-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. ERP Produktion" className="w-[240px] bg-card" />
          </div>
          <Button data-testid="api-key-create-button" onClick={erstellen} disabled={creating || !name}>
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Erstellen
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Aktive Keys</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length ? (
            <div className="overflow-x-auto">
              <Table data-testid="api-keys-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead>Zuletzt verwendet</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <span className="font-num text-xs bg-secondary px-2 py-1 rounded">{r.key.slice(0, 18)}…{r.key.slice(-4)}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtZeit(r.erstellt_am)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.zuletzt_verwendet ? fmtZeit(r.zuletzt_verwendet) : <Badge variant="outline">nie</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`api-key-copy-button-${r.name}`} variant="ghost" size="icon" title="Kopieren" onClick={() => kopieren(r.key)}><Copy className="h-4 w-4" /></Button>
                          <Button data-testid={`api-key-delete-button-${r.name}`} variant="ghost" size="icon" title="Löschen" onClick={() => loeschen(r)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={KeyRound} title="Noch keine API-Keys erstellt." />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Verfügbare ERP-Endpunkte</CardTitle>
          <CardDescription>Alle Endpunkte erwarten den Header X-API-Key. POST-Endpunkte arbeiten idempotent (Upsert).</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Methode</TableHead><TableHead>Pfad</TableHead><TableHead>Beschreibung</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {ENDPOINTS.map(([m, p, b]) => (
                <TableRow key={p}>
                  <TableCell><Badge variant="outline" className="font-num">{m}</Badge></TableCell>
                  <TableCell className="font-num text-sm">{p}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{b}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
